/**
 * The KPI dashboard's exports (A-02): the columns for its rankings, queues and sales trend, shared
 * by the dashboard cards and the top-100 page (components/ui/ExportButton writes the file).
 */

import type { KpiSeriesPoint, QueuedOrder, RankedCustomer, RankedMedicine, SeriesBucket } from "../api/analytics";
import { sheetDate, sheetDateTime, type ExportColumn } from "./tableExport";

/** A share (0.4286) as a percentage with two decimals (42.86). */
const percent = (share: number) => Math.round(share * 10000) / 100;

export const MEDICINE_RANK_EXPORT: ExportColumn<RankedMedicine>[] = [
  { header: "Rank", value: (m) => m.rank },
  { header: "Previous rank", value: (m) => m.previous_rank },
  { header: "Medicine", value: (m) => m.name },
  { header: "Brand", value: (m) => m.brand },
  { header: "Unit", value: (m) => m.unit },
  { header: "Units sold", value: (m) => m.units_sold },
  { header: "Orders", value: (m) => m.orders },
  { header: "Customers", value: (m) => m.customers },
  { header: "Revenue (₹)", value: (m) => m.revenue },
  { header: "Share of units %", value: (m) => percent(m.unit_share) },
  { header: "Share of revenue %", value: (m) => percent(m.revenue_share) },
];

// Phones arrive masked to their last 4 digits, and are exported that way.
export const CUSTOMER_RANK_EXPORT: ExportColumn<RankedCustomer>[] = [
  { header: "Rank", value: (c) => c.rank },
  { header: "Customer", value: (c) => c.name },
  { header: "Phone (masked)", value: (c) => c.phone_masked },
  { header: "Orders", value: (c) => c.orders },
  { header: "Spend (₹)", value: (c) => c.spend },
  { header: "Average order (₹)", value: (c) => c.average_order_value },
  { header: "Last order (IST)", value: (c) => sheetDateTime(c.last_order_at) },
  { header: "First order ever (IST)", value: (c) => sheetDateTime(c.first_order_at) },
  { header: "New customer", value: (c) => c.is_new },
];

export const QUEUED_ORDER_EXPORT: ExportColumn<QueuedOrder>[] = [
  { header: "Order", value: (o) => o.order_id },
  { header: "Patient", value: (o) => o.patient_name },
  { header: "Pharmacist", value: (o) => o.assignee_name ?? "Unclaimed" },
  { header: "Amount (₹)", value: (o) => o.final_amount },
  { header: "Placed (IST)", value: (o) => sheetDateTime(o.placed_at) },
  { header: "In this status since (IST)", value: (o) => sheetDateTime(o.in_status_since) },
  { header: "Stale", value: (o) => o.stale },
];

/** The sales trend, a row per hour (today) or per day. */
export function salesSeriesExport(bucket: SeriesBucket): ExportColumn<KpiSeriesPoint>[] {
  return [
    {
      header: bucket === "hour" ? "Hour (IST)" : "Day",
      value: (p) => (bucket === "hour" ? sheetDateTime(p.start) : sheetDate(p.start)),
    },
    { header: "Orders placed", value: (p) => p.orders },
    { header: "Paid orders", value: (p) => p.paid_orders },
    { header: "Sales (₹)", value: (p) => p.revenue },
  ];
}
